import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, switchMap, map } from 'rxjs/operators';
import * as accountsActions from '../actions/accounts.actions';
import { of } from 'rxjs';
import { AccountsService } from '../../services/accounts/accounts.service';
import { Account } from '../../models/account';
import { ShowErrorToast, ShowSuccessToast } from 'app/shared/modules/toast/store/actions/toast.actions';
import { RemoveKyc } from '../actions/accounts.actions';
import { UsersService } from '../../services/users.service';
import { User } from '../../models/user';

@Injectable()
export class AccountsEffects {
    searchAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(accountsActions.AccountsActionTypes.searchAccounts),
            exhaustMap((action: { limit: number; searchString: string; nftId?: string }) =>
                this.accountsService.searchAccounts(action.searchString, action.limit, action.nftId).pipe(
                    switchMap((res: any) =>
                        of(
                            accountsActions.SearchAccountsSuccess({
                                list: res.accounts,
                            })
                        )
                    ),
                    catchError((error: any) => of(accountsActions.SearchAccountsFailure(error)))
                )
            )
        )
    );

    getPaginatedAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(accountsActions.AccountsActionTypes.getPaginatedAccounts),
            exhaustMap(
                (action: {
                    searchTerm: string;
                    pageSize: number;
                    pageNumber: number;
                    userType: string;
                    fromDate?: string;
                    toDate?: string;
                }) =>
                    this.userService
                        .getAllUsers(
                            action.pageSize,
                            action.pageNumber,
                            action.searchTerm,
                            action.userType,
                            action.fromDate,
                            action.toDate
                        )
                        .pipe(
                            switchMap((res: { data: Array<User>; pageNumber: number; pageSize: number; totalItems: number }) =>
                                of(
                                    accountsActions.GetPaginatedAccountsSuccess({
                                        list: res.data,
                                        total: res.totalItems,
                                    })
                                )
                            ),
                            catchError((error: any) => of(accountsActions.GetPaginatedAccountsFailure(error)))
                        )
            )
        )
    );

    getAccount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(accountsActions.AccountsActionTypes.getAccount),
            exhaustMap((action: { accountId: string }) =>
                this.accountsService.getAccount(action.accountId).pipe(
                    switchMap((account: Account) => of(accountsActions.GetAccountSuccess({ account }))),
                    catchError((error: any) => of(accountsActions.GetAccountFailure(error)))
                )
            )
        )
    );

    updateAccount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(accountsActions.AccountsActionTypes.updateAccount),
            exhaustMap((action: { accountId: string; account: Account }) =>
                this.accountsService.updateAccount(action.accountId, action.account).pipe(
                    switchMap((account: Account) => [
                        accountsActions.UpdateAccountSuccess({ account }),
                        ShowSuccessToast({ message: 'Account updated successfully' }),
                    ]),
                    catchError((error: any) => [
                        accountsActions.UpdateAccountFailure(error),
                        ShowErrorToast({ message: 'Account update failed' }),
                    ])
                )
            )
        )
    );

    enableAccount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(accountsActions.AccountsActionTypes.enableAccount),
            exhaustMap((action: { accountId: string }) =>
                this.accountsService.enableAccount(action.accountId).pipe(
                    switchMap((account: Account) => [
                        accountsActions.EnableAccountSuccess({ account }),
                        ShowSuccessToast({ message: `Account ${account.disabled ? 'disabled' : 'enabled'} successfully` }),
                    ]),
                    catchError((error: any) => [
                        accountsActions.EnableAccountFailure(error),
                        ShowErrorToast({ message: 'Account update failed' }),
                    ])
                )
            )
        )
    );

    RemoveKyc = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveKyc),
            switchMap((action: { userId: string; reason: string }) => {
                return this.accountsService.removeKyc(action.userId, action.reason).pipe(
                    switchMap((res) => {
                        return [ShowSuccessToast({ message: 'KYC removed successfully' })];
                    })
                );
            }),
            catchError((error) => {
                return [ShowErrorToast({ message: error.error.error.error.message })];
            })
        )
    );

    constructor(private actions$: Actions, private accountsService: AccountsService, private userService: UsersService) {}
}
